import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { StatusCodes } from 'http-status-codes';
import { getVerifiedWorkflowById, getWorkflowById } from '../API/workflow';
import { STATUS } from '../constants';
import {
  clearPatientAccessToken,
  getPatientAccessToken,
} from '../utils/patientAccessStorage';

export const UNAUTHORIZED_ERROR = 'unauthorized';

export const fetchWorkflowById = createAsyncThunk(
  'workflow/fetchWorkflowById',
  async (workflowId) => {
    const { error, data } = await getWorkflowById(workflowId);

    if (data && !error) {
      return data;
    }

    throw new Error(error);
  }
);

export const fetchVerifiedWorkflowById = createAsyncThunk(
  'workflow/fetchVerifiedWorkflowById',
  async (workflowId) => {
    if (!getPatientAccessToken(workflowId)) {
      throw new Error(UNAUTHORIZED_ERROR);
    }

    const { error, data, httpStatus } = await getVerifiedWorkflowById(
      workflowId
    );

    if (data && !error) {
      return data;
    }

    if (httpStatus === StatusCodes.UNAUTHORIZED) {
      clearPatientAccessToken(workflowId);
      throw new Error(UNAUTHORIZED_ERROR);
    }

    throw new Error(error);
  }
);

const workflowSlice = createSlice({
  name: 'workflow',
  initialState: {
    data: {},
    status: STATUS.DEFAULT,
    isVerified: false,
    isUnauthorized: false,
    verifiedWorkflowId: null,
    // The workflow `data` belongs to, so another link's data is never used.
    workflowId: null,
  },
  reducers: {
    setVerifiedWorkflow(state, action) {
      const { workflowId, workflow } = action.payload;

      state.data = workflow;
      state.workflowId = workflowId;
      state.status = STATUS.SUCCESS;
      state.isVerified = true;
      state.isUnauthorized = false;
      state.verifiedWorkflowId = workflowId;
    },
    // Called once a booking succeeds, so this session no longer offers it.
    markAppointmentBooked(state, action) {
      const { workflowId, startDateTime, endDateTime } = action.payload;

      if (state.workflowId !== workflowId) return;

      state.data.appointment = {
        ...state.data.appointment,
        isBooked: true,
        isCancelled: false,
        startDateTime,
        endDateTime: endDateTime || '',
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWorkflowById.fulfilled, (state, action) => {
      state.data = action.payload;
      state.workflowId = action.meta.arg;
      state.status = STATUS.SUCCESS;
      state.isVerified = false;
      state.verifiedWorkflowId = null;
    });

    builder.addCase(fetchWorkflowById.pending, (state) => {
      state.status = STATUS.PENDING;
    });

    builder.addCase(fetchWorkflowById.rejected, (state) => {
      state.status = STATUS.FAILURE;
    });

    builder.addCase(fetchVerifiedWorkflowById.fulfilled, (state, action) => {
      state.data = action.payload;
      state.workflowId = action.meta.arg;
      state.status = STATUS.SUCCESS;
      state.isVerified = true;
      state.isUnauthorized = false;
      state.verifiedWorkflowId = action.meta.arg;
    });

    builder.addCase(fetchVerifiedWorkflowById.pending, (state, action) => {
      state.status = STATUS.PENDING;

      if (state.verifiedWorkflowId !== action.meta.arg) {
        state.data = {};
        state.isVerified = false;
        state.verifiedWorkflowId = null;
      }
    });

    builder.addCase(fetchVerifiedWorkflowById.rejected, (state, action) => {
      state.status = STATUS.FAILURE;

      if (action.error?.message === UNAUTHORIZED_ERROR) {
        state.data = {};
        state.isVerified = false;
        state.isUnauthorized = true;
        state.verifiedWorkflowId = null;
      }
    });
  },
});

export const { setVerifiedWorkflow, markAppointmentBooked } =
  workflowSlice.actions;

// One shared empty value, so selectors and effects see a stable reference.
const NO_WORKFLOW = Object.freeze({});

// The loaded workflow, or empty when what's loaded belongs to another link.
export const getLoadedWorkflow = (workflowState, workflowId) =>
  workflowState.workflowId === workflowId ? workflowState.data : NO_WORKFLOW;

export const isWorkflowVerified = (workflowState, workflowId) =>
  Boolean(
    workflowState.isVerified && workflowState.verifiedWorkflowId === workflowId
  );
export default workflowSlice.reducer;
